/**
 * Admin Downloads Management
 * Manage downloadable files and categories
 */

import React, { useState, useEffect } from 'react';
import {
  Download,
  Plus,
  Edit,
  Trash2,
  Upload,
  Folder,
  FileText,
  Eye,
  Calendar,
  HardDrive,
} from 'lucide-react';

interface Category {
  id: number;
  name: string;
  description: string | null;
  file_count: number;
  is_visible: number;
}

interface DownloadItem {
  id: number;
  category_id: number;
  name: string;
  description: string | null;
  filename: string;
  file_size: number;
  file_type: string;
  version: string | null;
  visibility: 'public' | 'clients_only';
  expires_at: string | null;
  downloads_count: number;
  created_at: string;
}

export const Downloads: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'files' | 'categories'>('files');
  const [filterCategory, setFilterCategory] = useState<number | ''>('');
  const [filterVisibility, setFilterVisibility] = useState<string>('');

  useEffect(() => {
    loadCategories();
    loadDownloads();
  }, [filterCategory, filterVisibility]);

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/v1/admin/downloads/categories?includeHidden=true', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadDownloads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.append('category_id', filterCategory.toString());
      if (filterVisibility) params.append('visibility', filterVisibility);

      const response = await fetch(`/api/v1/admin/downloads?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setDownloads(data.data.downloads);
      }
    } catch (error) {
      console.error('Failed to load downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDownload = async (downloadId: number) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await fetch(`/api/v1/admin/downloads/${downloadId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadDownloads();
        loadCategories(); // Refresh counts
      }
    } catch (error) {
      console.error('Failed to delete download:', error);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (!confirm('Are you sure you want to delete this category? All files in this category will also be deleted.')) return;

    try {
      const response = await fetch(`/api/v1/admin/downloads/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadCategories();
      }
    } catch (error) {
      console.error('Failed to delete category:', error);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('zip') || fileType.includes('compressed')) return '📦';
    return '📎';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Download className="w-6 h-6" />
              Downloads Center
            </h1>
            <p className="text-slate-400 mt-1">
              Manage downloadable files
            </p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Upload File
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700/50">
        <button
          onClick={() => setSelectedTab('files')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'files'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Files ({downloads.length})
        </button>
        <button
          onClick={() => setSelectedTab('categories')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'categories'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Categories ({categories.length})
        </button>
      </div>

      {/* Files Tab */}
      {selectedTab === 'files' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
            <div className="flex gap-4">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value ? parseInt(e.target.value) : '')}
                className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <select
                value={filterVisibility}
                onChange={(e) => setFilterVisibility(e.target.value)}
                className="px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
              >
                <option value="">All Visibility</option>
                <option value="public">Public</option>
                <option value="clients_only">Clients Only</option>
              </select>
            </div>
          </div>

          {/* Files Table */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">File</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Category</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Size</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Visibility</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Downloads</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Expires</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : downloads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-400">
                      No files found
                    </td>
                  </tr>
                ) : (
                  downloads.map((download) => {
                    const category = categories.find((c) => c.id === download.category_id);
                    const isExpired = download.expires_at && new Date(download.expires_at) < new Date();

                    return (
                      <tr key={download.id} className="hover:bg-slate-700/30">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getFileIcon(download.file_type)}</span>
                            <div>
                              <div className="text-white font-medium flex items-center gap-2">
                                {download.name}
                                {download.version && (
                                  <span className="px-2 py-0.5 bg-orange-900 text-blue-300 rounded text-xs">
                                    v{download.version}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-slate-400">{download.filename}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="px-2 py-1 bg-slate-700/50 text-slate-300 rounded text-sm">
                            {category?.name || 'Unknown'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1 text-slate-300">
                            <HardDrive className="w-4 h-4" />
                            {formatFileSize(download.file_size)}
                          </div>
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2 py-1 rounded text-sm ${
                              download.visibility === 'public'
                                ? 'bg-green-900 text-green-300'
                                : 'bg-orange-900 text-blue-300'
                            }`}
                          >
                            {download.visibility === 'public' ? 'Public' : 'Clients Only'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1 text-slate-300">
                            <Download className="w-4 h-4" />
                            {download.downloads_count}
                          </div>
                        </td>
                        <td className="p-4">
                          {isExpired ? (
                            <span className="text-red-400 text-sm">Expired</span>
                          ) : download.expires_at ? (
                            <span className="text-slate-300 text-sm flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(download.expires_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-gray-500 text-sm">Never</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button
                              className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDownload(download.id)}
                              className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Categories Tab */}
      {selectedTab === 'categories' && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Name</th>
                <th className="text-left p-4 text-slate-300 font-medium">Description</th>
                <th className="text-left p-4 text-slate-300 font-medium">Files</th>
                <th className="text-left p-4 text-slate-300 font-medium">Visible</th>
                <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-slate-400">
                    No categories found
                  </td>
                </tr>
              ) : (
                categories.map((category) => (
                  <tr key={category.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-white font-medium">
                        <Folder className="w-4 h-4 text-orange-400" />
                        {category.name}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-400">
                        {category.description || '-'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-slate-300">{category.file_count}</span>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded text-sm ${
                          category.is_visible
                            ? 'bg-green-900 text-green-300'
                            : 'bg-slate-700/50 text-slate-400'
                        }`}
                      >
                        {category.is_visible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Downloads;
